import * as React from 'react'
import styled, { css } from 'styled-components'
import { CartItem as sw6CartItem } from 'hooks/account/types'
import theme from 'theme'
import ProductImage from 'theme/atoms/ProductImage'
import ItemInfo from './ItemInfo'
import AmountChanger from './AmountChanger'
import PriceInfo from './PriceInfo'
import Link from 'theme/atoms/Link'
import getProductLink from 'utils/getProductLink'
import useTranslations from 'hooks/useTranslations'
import { useSnackbar } from 'modules/Snackbar'

type Props = {
  item: sw6CartItem
  updateItem: (amount: number) => void
  /** removes the 1px border at bottom */
  noBorder?: boolean
  noLink?: boolean
  disableHandles?: boolean
  channelBlocked?: boolean
}

export default function CartItem(props: Props) {
  const t = useTranslations<'molecules-account-CartItem'>()
  const snackbar = useSnackbar()
  return (
    <Wrapper
      className="CartItem"
      $noBorder={props.noBorder}
      data-cy-ctx="molecules/account/CartItem"
      $channelBlocked={props.channelBlocked || false}
    >
      <div className="img-wrapper">
        <Link to={getProductLink(props.item, props.noLink)}>
          <ProductImage
            img={props.item.images.imageWeb[0]}
            size="s"
            alt={props.item.title}
          />
        </Link>
      </div>
      <div className="content">
        <ItemInfo
          noLink={props.noLink}
          item={props.item}
          channelBlocked={props.channelBlocked || false}
          parentIsCartOrCheckout={true}
          snackbar={snackbar || null}
        />
        <div className="prices-mobile">
          <PriceInfo item={props.item} />
          <div style={{ flex: 1 }} />
        </div>

        {props.disableHandles ? (
          <div className="amount" data-cy-state="is-handles-disabled">
            {t('num_items', {
              COUNT: props.item.quantity,
              __transformers: {
                COUNT: (label) => <span className="normal">{label}</span>
              }
            })}
          </div>
        ) : (
          <AmountChanger item={props.item} updateItem={props.updateItem} />
        )}
      </div>
      <div className="prices-desktop">
        <PriceInfo item={props.item} />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $channelBlocked: boolean; $noBorder?: boolean }>`
  padding: ${theme.spacing('m')};
  ${(p) =>
    p.$channelBlocked
      ? `background: ${theme.colors.accent.info}`
      : `background: ${theme.colors.white}`};

  ${(p) =>
    p.$channelBlocked
      ? `border: 1px solid ${theme.colors.accent.blue}`
      : !p.$noBorder
        ? css`
            border-bottom: 1px solid ${theme.colors.shade.b5};
          `
        : ''};
  display: flex;
  gap: ${theme.spacing('xs')};

  > .img-wrapper {
    min-width: 80px;
    width: 80px;
    height: 80px;
    padding: 10px;
    background: white;
    border: 1px solid ${theme.colors.shade.b5};
  }

  > .content {
    flex: 1;
    display: flex;
    flex-direction: column;

    > .ItemInfo {
      margin-bottom: ${theme.spacing('xs')};
      @media (min-width: ${theme.ms.SM}px) {
        margin-bottom: ${theme.spacing('s')};
      }
    }

    > .prices-mobile {
      display: flex;
      margin-bottom: ${theme.spacing('s')};
      @media (min-width: ${theme.ms.SM}px) {
        display: none;
      }
    }

    > .amount {
      ${theme.ty('r-xs', 'bold', 'r-s')}
      .normal {
        font-weight: normal;
      }
    }
  }

  > .prices-desktop {
    display: none;
    @media (min-width: ${theme.ms.SM}px) {
      display: block;
    }
  }
`
